import { useQuery } from "@tanstack/react-query";
import { Paper, Skeleton, Stack, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts";
import { useSelector } from "react-redux";

import ErrorSection from "../../../components/alerts/ErrorSection";
import fetching from "../../../helpers/fetching/fetching";
import type { HeardAboutStat, UserState } from "../../../types/types";
import {
  buildHeardAboutStatsEndpoint,
  getHeardAboutPieData,
} from "../../statistics/heardAboutStats";

type Props = {
  from: string;
  to: string;
};

const HeardAboutDonutChart = ({ from, to }: Props) => {
  const userToken = useSelector((state: UserState) => state.user.userToken);
  const {
    data = [],
    isLoading,
    isError,
    error,
  } = useQuery<HeardAboutStat[]>({
    queryKey: ["statistics-heard-about", "home", from, to],
    queryFn: () =>
      fetching(
        buildHeardAboutStatsEndpoint("/statistics/heard-about", from, to),
        userToken,
      ).then(async (response) => {
        const body = await response.json();
        if (!response.ok || body.status) throw body.message || "Error";
        return body;
      }),
  });

  const pieData = getHeardAboutPieData(data);

  return (
    <Paper sx={{ p: 3 }}>
      <Stack gap={2}>
        <Typography variant="h5">كيف سمع المستخدمون عن مقصود؟</Typography>
        {isLoading ? (
          <Skeleton variant="rounded" height={360} />
        ) : isError ? (
          <ErrorSection errorMsg={String(error)} />
        ) : pieData.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={8}>
            لا توجد بيانات خلال الفترة المحددة
          </Typography>
        ) : (
          <PieChart
            height={360}
            series={[
              {
                data: pieData,
                innerRadius: 65,
                outerRadius: 120,
                paddingAngle: 2,
                cornerRadius: 4,
                valueFormatter: (item) => `${item.value} مستخدم`,
              },
            ]}
            slotProps={{
              legend: {
                direction: "column",
                position: { vertical: "middle", horizontal: "right" },
                padding: 0,
              },
            }}
          />
        )}
      </Stack>
    </Paper>
  );
};

export default HeardAboutDonutChart;
